import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import serviceClassificationService from '../../../services/serviceClassificationService';
import { useServiceClassification } from '../../../contexts/ServiceClassificationContext';
import SubcategoryForm from '../../../components/Classification/SubcategoryForm';
import ClassificationExcelToolbar from '../../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import ServicesClassificationNav from './ServicesClassificationNav';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../../hooks/useClassificationHierarchyForBulk';

const ServicesSubcategories = () => {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const canMutate = useCanMutate();
  const { categories, subcategories, loading, error, refetch } = useServiceClassification();
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    serviceClassificationService,
    subcategories.length
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    if (categoryId) setSelectedCategory(categoryId);
  }, [categoryId]);

  const filteredByCategory = useMemo(() => {
    if (!selectedCategory) return subcategories;
    return subcategories.filter(
      (s) => String(s.service_category_id) === String(selectedCategory) || String(s.category_id) === String(selectedCategory)
    );
  }, [subcategories, selectedCategory]);

  const filteredSubcategories = useMemo(
    () =>
      filteredByCategory.filter(
        (s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.code.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [filteredByCategory, searchTerm]
  );

  const bulk = useClassificationBulkDelete({
    variant: 'subcategories',
    rows: filteredSubcategories,
    hierarchy: fullHierarchy,
    deleteItem: (id) => serviceClassificationService.deleteSubcategory(id),
    onDeleted: async () => {
      await refetch();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  const selectedCategoryName = selectedCategory
    ? categories.find((c) => c.id === parseInt(selectedCategory, 10))?.name
    : 'Todas';

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Subcategorías (servicios)</h1>
              <p className="text-gray-600 mt-1">Nivel 2 — bajo una categoría de servicios</p>
              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
              {selectedCategory && (
                <p className="text-sm text-teal-600 mt-1">
                  Categoría: <span className="font-medium">{selectedCategoryName}</span>
                </p>
              )}
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <ClassificationExcelToolbar
                variant="subcategories"
                onImported={refetch}
                setToast={setToast}
                apiService={serviceClassificationService}
                filenameSuffix="servicios"
                importTitleSuffix=" (servicios)"
                exportRows={filteredSubcategories}
                exportContext={{ categories }}
              />
              <button
                type="button"
                onClick={() => {
                  setEditingSubcategory(null);
                  setIsFormOpen(true);
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium"
              >
                + Nueva subcategoría
              </button>
            </div>
          </div>

          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
            <span className="mx-2">/</span>
            <Link to="/classification/services/categories" className="hover:text-teal-600">Servicios</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Subcategorías</span>
          </nav>

          <ServicesClassificationNav />

          <div className="flex flex-nowrap items-end gap-3 mb-4 w-full overflow-x-auto pb-0.5">
            <div className="flex flex-col gap-1 flex-shrink-0 w-48">
              <label className="text-xs font-medium text-gray-600">Categoría</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Todas</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.code} — {cat.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[12rem]">
              <label className="text-xs font-medium text-gray-600">Buscar</label>
              <input
                type="text"
                placeholder="Buscar…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>

        <ClassificationBulkDeleteToolbar bulk={bulk} />

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          {filteredSubcategories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay subcategorías con los filtros actuales</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <ClassificationBulkDeleteTh bulk={bulk} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubcategories.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <ClassificationBulkDeleteTd bulk={bulk} row={sub} />
                    <td className="px-6 py-3 text-sm font-mono">{sub.code}</td>
                    <td className="px-6 py-3 text-sm text-gray-900">{sub.name}</td>
                    <td className="px-6 py-3 text-sm text-teal-700">
                      {sub.category ? `${sub.category.code} — ${sub.category.name}` : '—'}
                    </td>
                    <td className="px-6 py-3 text-sm">
                      {sub.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Activo</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Inactivo</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm font-medium">
                      <Link
                        to={`/classification/services/familias?subcategory=${sub.id}`}
                        className="text-teal-600 hover:text-teal-900 mr-3"
                      >
                        Familias
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSubcategory(sub);
                          setIsFormOpen(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSubcategoryToDelete(sub);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <SubcategoryForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingSubcategory(null);
        }}
        subcategory={editingSubcategory}
        onSuccess={() => refetch()}
        apiService={serviceClassificationService}
        preloadedCategories={categories}
      />

      <ClassificationBulkDeleteModal bulk={bulk} />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSubcategoryToDelete(null);
        }}
        onConfirm={async () => {
          if (!subcategoryToDelete) return;
          try {
            const response = await serviceClassificationService.deleteSubcategory(subcategoryToDelete.id);
            if (response.success) {
              setIsDeleteModalOpen(false);
              setSubcategoryToDelete(null);
              setToast({ isOpen: true, message: 'Subcategoría eliminada', type: 'success' });
              await refetch();
            } else {
              setToast({ isOpen: true, message: response.message || 'Error', type: 'error' });
            }
          } catch (e) {
            setToast({ isOpen: true, message: e.response?.data?.message || 'Error al eliminar', type: 'error' });
          }
        }}
        title="Eliminar subcategoría"
        message={subcategoryToDelete ? `¿Eliminar "${subcategoryToDelete.name}" (${subcategoryToDelete.code})?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ServicesSubcategories;
